import { getPaymentInfo } from '@/redux/actions/payment.action';
import { loggerSendingData } from '@/redux/actions/logger.action';

const ERROR_TRANSLATIONS: { [key: string]: string } = {
  // YooKassa official cancellation reasons
  '3d_secure_failed': 'Не пройдена аутентификация по 3‑D Secure. Повторите оплату и пройдите аутентификацию или используйте другое платежное средство',
  'call_issuer': 'Оплата отклонена. Обратитесь в банк, выпустивший карту',
  'canceled_by_merchant': 'Платеж отменен магазином',
  'card_expired': 'Срок действия карты истек. Используйте другую карту',
  'country_forbidden': 'Оплата картой, выпущенной в этой стране, запрещена',
  'deal_expired': 'Срок жизни сделки истек. Создайте новую сделку и оплату',
  'expired_on_capture': 'Истек срок списания по двухстадийной оплате. Повторите платеж',
  'expired_on_confirmation': 'Истек срок подтверждения оплаты. Повторите платеж',
  'fraud_suspected': 'Платеж заблокирован из‑за подозрения в мошенничестве',
  'general_decline': 'Оплата отклонена. Обратитесь к инициатору отмены',
  'identification_required': 'Требуется идентификация кошелька ЮMoney',
  'insufficient_funds': 'Недостаточно средств на карте',
  'internal_timeout': 'Техническая ошибка на стороне ЮKassa. Повторите позже',
  'invalid_card_number': 'Неверно указан номер карты',
  'invalid_csc': 'Неверный код CVV/CVC',
  'issuer_unavailable': 'Банк-эмитент недоступен. Повторите позже или используйте другой способ',
  'payment_method_limit_exceeded': 'Превышен лимит по способу оплаты. Повторите завтра или используйте другой способ',
  'payment_method_restricted': 'Операции этим платежным средством запрещены. Обратитесь в банк',
  'permission_revoked': 'Пользователь отозвал разрешение на автоплатежи. Создайте новый платеж и подтвердите его',
  'unsupported_mobile_operator': 'Нельзя заплатить с номера этого мобильного оператора',
  'card_declined': 'Карта отклонена банком',
  'expired_card': 'Срок действия карты истек',
  'invalid_card': 'Неверные данные карты',
  'card_not_supported': 'Карта не поддерживается',
  'payment_method_not_supported': 'Способ оплаты не поддерживается',
  'user_canceled': 'Пользователь отменил платеж',
  'canceled_by_user': 'Платеж отменён пользователем',
  'cancelled_by_user': 'Платеж отменён пользователем',
  'authorization_rejected': 'Банк отклонил платеж',
  'canceled': 'Платеж отменен',
  'timeout': 'Превышено время ожидания',
  'network_error': 'Ошибка сети',
  'technical_error': 'Техническая ошибка',
  'insufficient_funds_error': 'Недостаточно средств на карте',
  'insufficient_funds_on_card': 'Недостаточно средств на карте',
  'card_has_insufficient_funds': 'Недостаточно средств на карте',
  'insufficient_balance': 'Недостаточно средств на карте',
  'balance_insufficient': 'Недостаточно средств на карте',
  'unknown': 'Произошла неизвестная ошибка',
};

export interface IErrorCharacterization {
  errorType: 'fail' | 'error' | 'cancel' | 'timeout' | 'unknown';
  reason: string;
  orderId: string | null;
}

// Специальные случаи ошибок
const SPECIAL_ERRORS: { [key: string]: (paymentId?: string, paymentCreationError?: any) => IErrorCharacterization } = {
  'timeout': (paymentId) => ({
    errorType: 'timeout',
    reason: 'Превышено время ожидания оплаты',
    orderId: paymentId || null,
  }),
  'polling_error': (paymentId) => ({
    errorType: 'error',
    reason: 'Ошибка проверки статуса платежа',
    orderId: paymentId || null,
  }),
  'payment_creation_error': (_paymentId, paymentCreationError) => ({
    errorType: 'error',
    reason: paymentCreationError || 'Ошибка создания платежа',
    orderId: null,
  }),
  'user_data_error': () => ({
    errorType: 'error',
    reason: 'Не удалось получить данные пользователя',
    orderId: null,
  }),
  'unexpected_error': (_paymentId, paymentCreationError) => ({
    errorType: 'error',
    reason: paymentCreationError || 'Неожиданная ошибка',
    orderId: null,
  }),
  'webview_load_error': (paymentId) => ({
    errorType: 'error',
    reason: 'Ошибка загрузки страницы оплаты',
    orderId: paymentId || null,
  }),
};

export async function getErrorCharacterization(
  url?: string,
  paymentId?: string,
  paymentCreationError?: any
): Promise<IErrorCharacterization> {
  const safeUrl = typeof url === 'string' ? url : '';

  // Проверяем специальные случаи
  if (SPECIAL_ERRORS[safeUrl]) {
    return SPECIAL_ERRORS[safeUrl](paymentId, paymentCreationError);
  }

  let errorType: 'fail' | 'error' | 'cancel' | 'timeout' | 'unknown' = 'unknown';
  let reason = 'Не указана';
  let orderId: string | null = null;

  // Определение типа ошибки по URL
  if (safeUrl.includes('fail') || safeUrl.includes('failure')) {
    errorType = 'fail';
  } else if (safeUrl.includes('error')) {
    errorType = 'error';
  } else if (safeUrl.includes('cancel')) {
    errorType = 'cancel';
  }

  try {
    if (safeUrl && safeUrl.startsWith('http')) {
      const urlObj = new URL(safeUrl);

      orderId = urlObj.searchParams.get('orderId') || urlObj.searchParams.get('order_id') || null;

      reason =
        urlObj.searchParams.get('reason') ||
        urlObj.searchParams.get('error') ||
        urlObj.searchParams.get('message') ||
        urlObj.searchParams.get('description') ||
        urlObj.searchParams.get('error_description') ||
        'Не указана';
    }

    // Проверка статуса платежа через API
    if ((!safeUrl && paymentId) || orderId) {
      const idToCheck = orderId || paymentId;
      try {
        if (idToCheck) {
          const paymentInfo = await getPaymentInfo(idToCheck);
          if (paymentInfo && paymentInfo.status === 'canceled') {
            errorType = 'cancel';
            reason = paymentInfo.cancellation_details?.reason || 'Платеж отменен';
          }
        }
      } catch (apiError) {
        console.error('Error getting payment info:', apiError);
      }
    }

    // Перевод причины ошибки
    if (reason !== 'Не указана') {
      const lowerReason = String(reason).toLowerCase();
      if (
        lowerReason.includes('insufficient') ||
        lowerReason.includes('недостаточно') ||
        lowerReason.includes('средств') ||
        lowerReason.includes('balance') ||
        lowerReason.includes('funds')
      ) {
        reason = 'Недостаточно средств на карте';
      } else {
        reason = ERROR_TRANSLATIONS[lowerReason] || ERROR_TRANSLATIONS[reason] || reason;
      }
    }
  } catch (error) {
    console.error('Error parsing URL for reason:', error);
    reason = 'Ошибка парсинга URL';
  }

  return { errorType, reason, orderId };
}

export async function logWebViewError(
  kind: string,
  nativeEvent: any,
  paymentId: string | undefined,
  url: string | undefined,
  lastUrl: string | undefined,
  phone: string | undefined,
  route: any,
  Platform: any
): Promise<void> {
  try {
    const payload = {
      kind,
      nativeEvent,
      paymentId,
      url,
      lastUrl,
      phone,
      route,
      platform: Platform?.OS || 'web',
    };

    await loggerSendingData({
      type: 'webview_error',
      data: payload,
    });
  } catch (error) {
    console.error('Error logging WebView error:', error);
  }
}
